import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuthStore } from '../stores/authStore'

interface ChartOfAccountsAccount {
  code: string
  name: string
  rootType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE'
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE'
  isGroup: boolean
  parentCode?: string
  isBankAccount?: boolean
  currency?: string
  openingBalance?: number
}

interface BankAccount {
  accountName: string
  accountNumber?: string
  bankName?: string
  accountCode: string
  currency: string
  openingBalance: number
}

interface WizardStep {
  id: string
  title: string
  description: string
}

const STEPS: WizardStep[] = [
  { id: 'welcome', title: 'Welcome', description: 'Get started with your accounting setup' },
  { id: 'chart-of-accounts', title: 'Chart of Accounts', description: 'Set up your account structure' },
  { id: 'bank-accounts', title: 'Bank Accounts', description: 'Configure your bank accounts' },
  { id: 'organization', title: 'Organization', description: 'Finalize organization settings' },
  { id: 'review', title: 'Review', description: 'Review and complete setup' },
]

const PRESET_CHART_OF_ACCOUNTS: ChartOfAccountsAccount[] = [
  // Assets
  { code: '1000', name: 'Business Bank Account', rootType: 'ASSET', accountType: 'ASSET', isGroup: false, isBankAccount: true, currency: 'GBP' },
  { code: '1010', name: 'Business Savings Account', rootType: 'ASSET', accountType: 'ASSET', isGroup: false, isBankAccount: true, currency: 'GBP' },
  { code: '1020', name: 'Petty Cash', rootType: 'ASSET', accountType: 'ASSET', isGroup: false, currency: 'GBP' },
  { code: '1100', name: 'PayPal Account', rootType: 'ASSET', accountType: 'ASSET', isGroup: false, isBankAccount: true, currency: 'GBP' },
  { code: '2030', name: 'Accounts Receivable', rootType: 'ASSET', accountType: 'ASSET', isGroup: false, currency: 'GBP' },
  
  // Liabilities
  { code: '3000', name: 'Accounts Payable', rootType: 'LIABILITY', accountType: 'LIABILITY', isGroup: false, currency: 'GBP' },
  { code: '3010', name: 'Credit Card Payable', rootType: 'LIABILITY', accountType: 'LIABILITY', isGroup: false, currency: 'GBP' },
  { code: '3030', name: 'Taxes Payable', rootType: 'LIABILITY', accountType: 'LIABILITY', isGroup: false, currency: 'GBP' },
  
  // Equity
  { code: '9000', name: "Owner's Capital", rootType: 'EQUITY', accountType: 'EQUITY', isGroup: false, currency: 'GBP' },
  { code: '9100', name: 'Retained Earnings', rootType: 'EQUITY', accountType: 'EQUITY', isGroup: false, currency: 'GBP' },
  
  // Income
  { code: '4000', name: 'Sales Revenue', rootType: 'INCOME', accountType: 'INCOME', isGroup: false, currency: 'GBP' },
  { code: '4100', name: 'Service Revenue', rootType: 'INCOME', accountType: 'INCOME', isGroup: false, currency: 'GBP' },
  
  // Expenses
  { code: '5000', name: 'Office Supplies', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
  { code: '5100', name: 'Rent', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
  { code: '5200', name: 'Utilities', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
  { code: '5300', name: 'Software Licensing', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
  { code: '5400', name: 'Marketing Expenses', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
  { code: '5600', name: 'Salaries', rootType: 'EXPENSE', accountType: 'EXPENSE', isGroup: false, currency: 'GBP' },
]

const SetupWizard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Chart of Accounts state
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccountsAccount[]>(PRESET_CHART_OF_ACCOUNTS)
  const [editingAccount, setEditingAccount] = useState<number | null>(null)
  
  // Bank Accounts state
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([
    { accountName: 'Business Bank Account', accountCode: '1000', currency: 'GBP', openingBalance: 0 }
  ])
  
  // Organization state
  const [orgSettings, setOrgSettings] = useState({
    fiscalYearStart: '01-01',
    defaultCurrency: 'GBP',
    taxRegistrationNumber: '',
  })

  useEffect(() => {
    // Check if user has permission
    if (!user) {
      navigate('/login')
    }
  }, [user, navigate])

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
      setError('')
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      setError('')
    }
  }

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to exit the setup wizard? Your changes will not be saved.')) {
      navigate('/erp/chart-of-accounts')
    }
  }

  const handleAccountEdit = (index: number) => {
    setEditingAccount(index)
  }

  const handleAccountSave = (index: number, updatedAccount: ChartOfAccountsAccount) => {
    const updated = [...chartOfAccounts]
    updated[index] = updatedAccount
    setChartOfAccounts(updated)
    setEditingAccount(null)
  }

  const handleAccountDelete = (index: number) => {
    const updated = chartOfAccounts.filter((_, i) => i !== index)
    setChartOfAccounts(updated)
  }

  const handleAddAccount = () => {
    setChartOfAccounts([
      ...chartOfAccounts,
      {
        code: '',
        name: '',
        rootType: 'ASSET',
        accountType: 'ASSET',
        isGroup: false,
        currency: 'GBP',
      }
    ])
    setEditingAccount(chartOfAccounts.length)
  }

  const handleBankAccountAdd = () => {
    setBankAccounts([
      ...bankAccounts,
      { accountName: '', accountCode: '', currency: 'GBP', openingBalance: 0 }
    ])
  }

  const handleBankAccountUpdate = (index: number, field: keyof BankAccount, value: any) => {
    const updated = [...bankAccounts]
    updated[index] = { ...updated[index], [field]: value }
    setBankAccounts(updated)
  }

  const handleBankAccountDelete = (index: number) => {
    setBankAccounts(bankAccounts.filter((_, i) => i !== index))
  }

  const handleComplete = async () => {
    setLoading(true)
    setError('')
    
    try {
      // Step 1: Create chart of accounts
      const accountsResponse = await axios.post('/api/erp/accounts/bulk-create', {
        accounts: chartOfAccounts.map(acc => ({
          code: acc.code,
          name: acc.name,
          accountType: acc.accountType,
          rootType: acc.rootType,
          isGroup: acc.isGroup,
          isBankAccount: acc.isBankAccount || false,
          currency: acc.currency || 'GBP',
          openingBalance: acc.openingBalance || 0,
        }))
      })
      
      if (!accountsResponse.data.success) {
        throw new Error('Failed to create accounts: ' + JSON.stringify(accountsResponse.data.data?.errors))
      }
      
      // Step 2: Get created accounts to find IDs by code
      const accountsListResponse = await axios.get('/api/erp/accounts')
      const createdAccounts = accountsListResponse.data.data || []
      
      // Step 3: Create bank accounts (need to find account ID by code)
      for (const bankAccount of bankAccounts) {
        if (bankAccount.accountName && bankAccount.accountCode) {
          // Find the account ID by code
          const linkedAccount = createdAccounts.find((acc: any) => acc.code === bankAccount.accountCode)
          
          if (!linkedAccount) {
            console.warn(`Account with code ${bankAccount.accountCode} not found, skipping bank account creation`)
            continue
          }
          
          if (!linkedAccount.isBankAccount) {
            // Update the account to mark it as a bank account
            await axios.put(`/api/erp/accounts/${linkedAccount.id}`, {
              isBankAccount: true,
              allowReconciliation: true,
            })
          }
          
          // Create bank account
          await axios.post('/api/erp/bank-accounts', {
            accountId: linkedAccount.id,
            name: bankAccount.accountName,
            accountNumber: bankAccount.accountNumber || undefined,
            bankName: bankAccount.bankName || 'Unknown',
            accountType: 'Checking',
            currency: bankAccount.currency,
            openingBalance: bankAccount.openingBalance || 0,
          })
        }
      }
      
      // Step 4: Update organization settings if needed
      // This would go to a settings endpoint when available
      
      navigate('/erp/chart-of-accounts', { state: { setupComplete: true } })
    } catch (error: any) {
      setError(error.response?.data?.error || error.message || 'Failed to complete setup')
      console.error('Setup wizard error:', error)
    } finally {
      setLoading(false)
    }
  }

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to the Setup Wizard</h2>
              <p className="text-gray-600">
                This wizard will help you quickly set up your accounting system with pre-configured accounts and settings.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">What we'll set up:</h3>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Chart of Accounts with standard UK accounts</li>
                <li>Bank accounts configuration</li>
                <li>Organization settings</li>
                <li>Basic tax configuration</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> You can edit all preset values before completing the setup. 
                This wizard is designed to get you started quickly, but you have full control over every setting.
              </p>
            </div>
          </div>
        )

      case 'chart-of-accounts':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Chart of Accounts</h3>
              <p className="text-sm text-gray-600 mb-4">
                Review and edit the preset accounts. You can modify names, codes, or remove accounts you don't need.
              </p>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank Account</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {chartOfAccounts.map((account, index) => (
                      <tr key={index}>
                        {editingAccount === index ? (
                          <>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={account.code}
                                onChange={(e) => handleAccountSave(index, { ...account, code: e.target.value })}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                onBlur={() => setEditingAccount(null)}
                                autoFocus
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={account.name}
                                onChange={(e) => handleAccountSave(index, { ...account, name: e.target.value })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                onBlur={() => setEditingAccount(null)}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={account.rootType}
                                onChange={(e) => handleAccountSave(index, { ...account, rootType: e.target.value as any, accountType: e.target.value as any })}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                                onBlur={() => setEditingAccount(null)}
                              >
                                <option value="ASSET">Asset</option>
                                <option value="LIABILITY">Liability</option>
                                <option value="EQUITY">Equity</option>
                                <option value="INCOME">Income</option>
                                <option value="EXPENSE">Expense</option>
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={account.isBankAccount || false}
                                onChange={(e) => handleAccountSave(index, { ...account, isBankAccount: e.target.checked })}
                                className="rounded"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setEditingAccount(null)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Done
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-sm text-gray-900">{account.code}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{account.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{account.rootType}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {account.isBankAccount ? 'Yes' : 'No'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleAccountEdit(index)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleAccountDelete(index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <button
              onClick={handleAddAccount}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
            >
              + Add Account
            </button>
          </div>
        )

      case 'bank-accounts':
        const bankAccountOptions = chartOfAccounts
          .filter(acc => acc.isBankAccount)
          .map(acc => ({ value: acc.code, label: `${acc.code} - ${acc.name}` }))
        
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Accounts</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure your bank accounts. Link them to accounts from your Chart of Accounts that are marked as bank accounts.
              </p>
              {bankAccountOptions.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> No bank accounts found in Chart of Accounts. Go back and mark some accounts as bank accounts.
                  </p>
                </div>
              )}
            </div>
            
            {bankAccounts.map((bankAccount, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                    <input
                      type="text"
                      value={bankAccount.accountName}
                      onChange={(e) => handleBankAccountUpdate(index, 'accountName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g., Business Bank Account"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chart of Accounts Code *</label>
                    {bankAccountOptions.length > 0 ? (
                      <select
                        value={bankAccount.accountCode}
                        onChange={(e) => handleBankAccountUpdate(index, 'accountCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select account...</option>
                        {bankAccountOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={bankAccount.accountCode}
                        onChange={(e) => handleBankAccountUpdate(index, 'accountCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="e.g., 1000"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={bankAccount.bankName || ''}
                      onChange={(e) => handleBankAccountUpdate(index, 'bankName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g., HSBC"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={bankAccount.accountNumber || ''}
                      onChange={(e) => handleBankAccountUpdate(index, 'accountNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={bankAccount.currency}
                      onChange={(e) => handleBankAccountUpdate(index, 'currency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bankAccount.openingBalance}
                      onChange={(e) => handleBankAccountUpdate(index, 'openingBalance', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                {bankAccounts.length > 1 && (
                  <button
                    onClick={() => handleBankAccountDelete(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove Bank Account
                  </button>
                )}
              </div>
            ))}
            
            <button
              onClick={handleBankAccountAdd}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
            >
              + Add Bank Account
            </button>
          </div>
        )

      case 'organization':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Organization Settings</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure basic organization settings for your accounting system.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fiscal Year Start</label>
                <input
                  type="text"
                  value={orgSettings.fiscalYearStart}
                  onChange={(e) => setOrgSettings({ ...orgSettings, fiscalYearStart: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="MM-DD (e.g., 01-01)"
                />
                <p className="mt-1 text-xs text-gray-500">Format: MM-DD (e.g., 01-01 for January 1st)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
                <select
                  value={orgSettings.defaultCurrency}
                  onChange={(e) => setOrgSettings({ ...orgSettings, defaultCurrency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="GBP">GBP - British Pound</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Registration Number</label>
                <input
                  type="text"
                  value={orgSettings.taxRegistrationNumber}
                  onChange={(e) => setOrgSettings({ ...orgSettings, taxRegistrationNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        )

      case 'review':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Your Setup</h3>
              <p className="text-sm text-gray-600 mb-4">
                Review all settings before completing the setup. You can go back to make changes.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Chart of Accounts</h4>
                <p className="text-sm text-gray-600">{chartOfAccounts.length} accounts will be created</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Bank Accounts</h4>
                <p className="text-sm text-gray-600">{bankAccounts.length} bank account(s) will be configured</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Organization Settings</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>Fiscal Year Start: {orgSettings.fiscalYearStart}</li>
                  <li>Default Currency: {orgSettings.defaultCurrency}</li>
                  {orgSettings.taxRegistrationNumber && (
                    <li>Tax Registration: {orgSettings.taxRegistrationNumber}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Cancel button */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleCancel}
            className="text-gray-600 hover:text-gray-800 text-sm flex items-center space-x-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Cancel</span>
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      index <= currentStep
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="mt-2 text-xs text-center text-gray-600 max-w-[100px]">
                    {step.title}
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      index < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{STEPS[currentStep].title}</h2>
            <p className="text-gray-600 mt-1">{STEPS[currentStep].description}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="min-h-[400px]">
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
            </div>
            
            {currentStep === STEPS.length - 1 ? (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Completing...' : 'Complete Setup'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SetupWizard
